import React, { useMemo } from 'react';
import { TUTORIAL_CAMPAIGN } from '../../constants';
import {
  getCareerSummary,
  getRecentRuns,
  getScenarioStats,
  formatModeLabel,
  formatOutcomeLabel,
  type RunCompletionRecord,
} from '../../utils/playerAnalytics';
import { type as typeTokens, surfaces, cn } from './kit';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: string;
}) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3 text-center">
      <p className={typeTokens.eyebrow}>{label}</p>
      <p className={cn('font-mono font-bold text-lg mt-1', accent ?? 'text-white')}>{value}</p>
    </div>
  );
}

function RunRow({ run }: { run: RunCompletionRecord }) {
  const won = run.outcome === 'won' || run.outcome === 'scenario_won';
  return (
    <li
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm rounded-md px-3 py-2 bg-slate-950/50 border border-slate-800/80"
      data-testid={`career-run-${run.id}`}
    >
      <div className="min-w-0">
        <p className="text-white font-medium truncate">{run.scenarioTitle}</p>
        <p className="text-xs text-slate-500 mt-0.5">
          {formatModeLabel(run.mode)} · {formatDate(run.date)}
        </p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span
          className={cn(
            'text-xs font-mono font-bold px-2 py-0.5 rounded',
            won ? 'text-emerald-400 bg-emerald-950/50' : 'text-red-400 bg-red-950/40',
          )}
        >
          {formatOutcomeLabel(run.outcome)}
        </span>
        <span className="font-mono font-bold text-cyan-300">{run.score.toFixed(1)}</span>
      </div>
    </li>
  );
}

interface PlayerStatsPanelProps {
  compact?: boolean;
}

const PlayerStatsPanel: React.FC<PlayerStatsPanelProps> = ({ compact = false }) => {
  const summary = useMemo(() => getCareerSummary(TUTORIAL_CAMPAIGN.length), []);
  const recentRuns = useMemo(() => getRecentRuns(compact ? 5 : 8), [compact]);
  const scenarioStats = useMemo(() => getScenarioStats(), []);

  if (summary.totalRuns === 0) {
    return (
      <div
        className={cn(surfaces.panelInset, 'p-6 text-center')}
        data-testid="player-stats-empty"
      >
        <p className={typeTokens.eyebrowAccent}>No career data yet</p>
        <p className="text-sm text-slate-400 mt-2">
          Complete a sandbox IPO, academy module, or scenario mission to build your professional record.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="player-stats-panel">
      <section>
        <h3 className={cn(typeTokens.eyebrow, 'mb-3')}>Career Summary</h3>
        <div className={cn('grid gap-3', compact ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4')}>
          <StatCard label="Total Runs" value={summary.totalRuns} accent="text-white" />
          <StatCard label="Win Rate" value={`${summary.winRate}%`} accent="text-emerald-400" />
          <StatCard label="Best Score" value={summary.bestOverallScore.toFixed(1)} accent="text-cyan-400" />
          <StatCard
            label="Academy"
            value={`${summary.trainingProgress}/${summary.trainingTotal}`}
            accent="text-indigo-300"
          />
        </div>
        {!compact && (
          <div className="grid grid-cols-3 gap-3 mt-3">
            <StatCard label="Sandbox" value={summary.sandboxRuns} />
            <StatCard label="Academy Runs" value={summary.trainingRuns} />
            <StatCard label="Missions" value={summary.missionRuns} />
          </div>
        )}
      </section>

      {scenarioStats.length > 0 && (
        <section>
          <h3 className={cn(typeTokens.eyebrow, 'mb-3')}>Per-Scenario Record</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {scenarioStats.map((stat) => (
              <div
                key={stat.scenarioId}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-slate-800 bg-slate-900/40 text-sm"
                data-testid={`career-scenario-${stat.scenarioId}`}
              >
                <div className="min-w-0">
                  <p className="text-white truncate">{stat.scenarioTitle}</p>
                  <p className="text-xs text-slate-500">
                    {formatModeLabel(stat.mode)} · {stat.attempts} attempt{stat.attempts === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-cyan-300 font-bold">{stat.bestScore.toFixed(1)}</p>
                  <p className="text-xs text-slate-500">
                    {stat.wins}W / {stat.losses}L
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {recentRuns.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className={typeTokens.eyebrow}>Recent Completions</h3>
            {!compact && summary.quizAttempts > 0 && (
              <span className={typeTokens.caption}>
                Quiz {summary.quizPasses}/{summary.quizAttempts} passed
              </span>
            )}
          </div>
          <ul className="space-y-2 max-h-56 overflow-y-auto">
            {recentRuns.map((run) => (
              <RunRow key={run.id} run={run} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default PlayerStatsPanel;